/**
 * The track FILE (version 2) and the two conversions between it and the editor's own state.
 *
 * The file used to be the editor's sample buffer: every curve flattened to 16 points, every segment's
 * endpoints stitched into one long `trackPath`, `gap` markers where the stitch didn't land exactly,
 * an order number per piece so the stitch knew where to put it, and the whole geometry written twice
 * (`trackPath` AND the `segments` it came from). Reading it back meant trying to recover curves out
 * of that approximation, which is where every defect of the last week came from.
 *
 * The file is now the MODEL: a short list of pieces, each a chain of nodes with their Bezier handles
 * and a width per node. Nothing is flattened, nothing is duplicated, and the order of the pieces is
 * just their order in the array — a lane is one piece that closes on itself, not forty segments that
 * have to be reordered by hand.
 *
 * `segs` and `areas` are still the editor's editing units (a drag creates a segment, a click a node);
 * `buildPieces` is the only place that knows how they add up to a piece.
 */
import { edgeControls } from './trackGeometry.js';

export const FORMAT_VERSION = 2;
const TOLERANCE = 0.01;

/**
 * The looks an asphalt piece can be painted with (`surface` on a piece, in the file): the painter
 * picks its textures from this name. Keep it in step with ./trackPainter.js (SURFACE_STYLES): a
 * name missing here is re-exported as the default, so the editor and the painter agreeing on this
 * list is what keeps a track from changing look on the way through.
 * The first entry is the default, i.e. what a piece with no `surface` at all is painted as.
 */
export const SURFACE_STYLES = ['gpo'];
export const DEFAULT_SURFACE = SURFACE_STYLES[0];

/** The `surface` of an editing unit (a segment or a road area), defaulted: the UI always shows one. */
export function surfaceOf(obj) {
  return SURFACE_STYLES.includes(obj?.surface) ? obj.surface : DEFAULT_SURFACE;
}

/** What goes in the file for that unit: nothing when it is the default, so old files stay old. */
function surfaceField(surface) {
  return surface && surface !== DEFAULT_SURFACE ? { surface } : {};
}

/**
 * The terrains a terrain area can be made of (`terrain` on a terrain area, in the file): the ground
 * painted over the grass — a rectangle, a circle or a free area, all closed figures. MUST stay in step
 * with the game's simulation catalogue (gpo2/src/server/config/physicsConfig.js, TERRAINS): the NAME is
 * the contract the file carries, the physics behind it belongs to the server.
 *
 * Each terrain says what the editor needs to offer it: the `label` it is listed under, the family it sits
 * in (grounds of the same kind together — every snow, every mud) and the flat `colour` its figures are
 * drawn with on the canvas. Its picture has no entry here: it is the file `textures/terrains/<name>.jpg`
 * by convention (see TrackSurroundings.terrainImageSrc).
 *
 * The first entry is the default and is never written to the file: a point on neither a road nor any
 * terrain area is grass, exactly as every track behaved before terrain areas existed. It is offered all
 * the same, so a figure can be set back to grass if the author wants to.
 */
const GRASS_TERRAINS = [
  { name: 'grass', label: 'Grass', colour: '#4f8a3a' },
  { name: 'fine_grass', label: 'Fine grass', colour: '#4a7f33', blend: true },
  { name: 'fine_grass2', label: 'Fine grass 2', colour: '#556226', blend: true },
  { name: 'lawn', label: 'Lawn', colour: '#5a6f25', blend: true },
];

export const TERRAIN_GROUPS = [
  { label: 'Grass', terrains: GRASS_TERRAINS },
  { label: 'Dirt', terrains: [
    { name: 'grass_dirt', label: 'Grass and dirt', colour: '#a9793f', blend: true },
    { name: 'dirt', label: 'Dirt', colour: '#977e64', blend: true },
    { name: 'dirt_grass', label: 'Dirt with grass', colour: '#5f582f', blend: true },
    { name: 'red_dirt', label: 'Red dirt', colour: '#9e6a3f', blend: true },
    { name: 'forest_floor', label: 'Forest floor', colour: '#53402e', blend: true },
  ] },
  { label: 'Mud', terrains: [
    { name: 'mud', label: 'Mud', colour: '#493222', blend: true },
    { name: 'mud_grass', label: 'Mud with grass', colour: '#5a4f37', blend: true },
  ] },
  { label: 'Sand', terrains: [
    { name: 'sand', label: 'Sand', colour: '#c9ae7c', blend: true },
    { name: 'sand2', label: 'Sand 2', colour: '#c2a878', blend: true },
    { name: 'compact_sand', label: 'Compact sand', colour: '#b39879', blend: true },
    { name: 'rocky_sand', label: 'Rocky sand', colour: '#b7a780', blend: true },
    { name: 'gravel', label: 'Compacted stones', colour: '#635c50', blend: true },
  ] },
  { label: 'Stone', terrains: [
    { name: 'cobblestone', label: 'Cobblestone', colour: '#8b8f98' },
    { name: 'cobblestone_grass_grid', label: 'Cobblestone grid', colour: '#7f8a6a' },
    { name: 'dense_paving', label: 'Dense paving', colour: '#887453' },
    { name: 'paving_grass', label: 'Paving with grass', colour: '#9e9781' },
  ] },
  { label: 'Snow', terrains: [
    { name: 'snow', label: 'Snow', colour: '#c6c6c6', blend: true },
    { name: 'snow_grass', label: 'Snow and grass', colour: '#a1a597', blend: true },
    { name: 'snow_dirt', label: 'Snow and dirt', colour: '#90867d', blend: true },
  ] },
];

const TERRAINS = TERRAIN_GROUPS.flatMap(group => group.terrains);

export const TERRAIN_STYLES = TERRAINS.map(terrain => terrain.name);
export const DEFAULT_TERRAIN = TERRAIN_STYLES[0];

/** The ground each terrain's figures are drawn with on the editor canvas. */
export const TERRAIN_COLOURS = Object.fromEntries(TERRAINS.map(terrain => [terrain.name, terrain.colour]));

/**
 * The grounds the base surface can be painted with (`grassTexture` on a track): the grass family alone,
 * because the ground outside every terrain area keeps driving as grass — a base that looked like snow but
 * gripped like grass would be a trap, so the picker offers only what looks like grass.
 */
export const GRASS_TEXTURES = GRASS_TERRAINS.map(terrain => terrain.name);
export const DEFAULT_GRASS_TEXTURE = GRASS_TEXTURES[0];

/** The texture a track paints its base ground with; a name outside the catalogue is the default grass. */
export function grassTextureOf(track) {
  return GRASS_TEXTURES.includes(track?.grassTexture) ? track.grassTexture : DEFAULT_GRASS_TEXTURE;
}

/** The `terrain` of a terrain area, defaulted: the UI always shows one. */
export function terrainOf(obj) {
  return TERRAIN_STYLES.includes(obj?.terrain) ? obj.terrain : DEFAULT_TERRAIN;
}

/**
 * Whether a ground figure fades into the ground it borders, per terrain — `blend` in the catalogue above.
 * Paving ends on its own outline (a cobbled area is a made surface with an edge); loose grounds — dirt,
 * sand, gravel — thin out into whatever is around them. A single figure can say otherwise with its own
 * `blend`, which is what the editor's checkbox writes; this table is only the default it starts from.
 */
export const TERRAIN_BLEND_DEFAULTS = Object.fromEntries(TERRAINS.map(terrain => [terrain.name, terrain.blend === true]));

/** The blend a terrain does by default; a name outside the catalogue keeps its figures' edges. */
export function terrainBlendDefault(terrain) {
  return TERRAIN_BLEND_DEFAULTS[terrain] === true;
}

/** The blend of one figure: its own `blend` when it carries one, else the default of its terrain. */
export function terrainBlendOf(area) {
  return typeof area?.blend === 'boolean' ? area.blend : terrainBlendDefault(terrainOf(area));
}

/**
 * The painting layer of a figure or an obstacle: its own `z` when it has one, 0 otherwise. Higher
 * paints later, i.e. on top — a figure with z=1 lies over one with no z (or z=0), while two without
 * one keep the order they have in the file.
 */
export function zOf(obj) {
  return Number.isFinite(obj?.z) ? obj.z : 0;
}

/**
 * `items` in painting order: by z, and the order in the array among equal z (a stable sort — a track
 * that uses no z paints exactly as it always has).
 */
export function inPaintOrder(items) {
  return (items ?? []).map((item, index) => ({ item, index }))
    .sort((a, b) => zOf(a.item) - zOf(b.item) || a.index - b.index)
    .map(({ item }) => item);
}

/** The same list cut into layers, lowest z first: each layer is painted complete before the next. */
export function inPaintLayers(items) {
  const layers = [];
  for (const item of inPaintOrder(items)) {
    const z = zOf(item);
    const last = layers[layers.length - 1];
    if (last && last.z === z) last.items.push(item);
    else layers.push({ z, items: [item] });
  }
  return layers;
}

/** The two ends of a chain are one node when they are this close: an export never lands exactly. */
const JOINT = 2;

/**
 * A segment as a cubic edge: the two endpoints, each with the handle it leaves/arrives with. A
 * segment with a single control point (`cx`/`cy`, the editor's way of drawing a curve) is exactly a
 * cubic with the handles two thirds of the way to it — the same curve, one representation.
 */
export function segEdge(s) {
  const dx = s.x2 - s.x1;
  const dy = s.y2 - s.y1;
  const thirds = {
    c1: { x: s.x1 + dx / 3, y: s.y1 + dy / 3 },
    c2: { x: s.x1 + (dx * 2) / 3, y: s.y1 + (dy * 2) / 3 },
  };
  if (s.cx != null) {
    return {
      a: { x: s.x1, y: s.y1 },
      b: { x: s.x2, y: s.y2 },
      c1: { x: s.x1 + (2 / 3) * (s.cx - s.x1), y: s.y1 + (2 / 3) * (s.cy - s.y1) },
      c2: { x: s.x2 + (2 / 3) * (s.cx - s.x2), y: s.y2 + (2 / 3) * (s.cy - s.y2) },
      straight: false,
    };
  }
  if (s.hOut || s.hIn) {
    return {
      a: { x: s.x1, y: s.y1 },
      b: { x: s.x2, y: s.y2 },
      c1: s.hOut ?? thirds.c1,
      c2: s.hIn ?? thirds.c2,
      straight: false,
    };
  }
  return { a: { x: s.x1, y: s.y1 }, b: { x: s.x2, y: s.y2 }, c1: thirds.c1, c2: thirds.c2, straight: true };
}

/** The single control point of a segment that is (near enough) a quadratic, else null. */
export function quadraticOf(edge) {
  const cx = edge.a.x + 1.5 * (edge.c1.x - edge.a.x);
  const cy = edge.a.y + 1.5 * (edge.c1.y - edge.a.y);
  const back = { x: edge.b.x + 1.5 * (edge.c2.x - edge.b.x), y: edge.b.y + 1.5 * (edge.c2.y - edge.b.y) };
  return Math.hypot(back.x - cx, back.y - cy) <= JOINT ? { x: cx, y: cy } : null;
}

/** The distance between two ends, and whether that counts as "close enough to be one road". */
const jointReach = (a, b, width) => JOINT + (a?.width ?? width ?? 0) / 2;

/**
 * The segments of a lane, in driving order: whichever unused segment has an end nearest to where the
 * road currently stops continues it (turned around if that is the end that fits). Nothing else — no
 * order number to keep up to date by hand, and no way to get it wrong: the editor draws segments one
 * after another, and this finds them again.
 */
export function chainSegments(segs, width) {
  const pending = (segs ?? []).filter(Boolean).map((seg) => ({ seg, flipped: false }));
  const runs = [];
  while (pending.length) {
    const run = [pending.shift()];
    for (;;) {
      const last = run[run.length - 1];
      const end = last.flipped ? { x: last.seg.x1, y: last.seg.y1 } : { x: last.seg.x2, y: last.seg.y2 };
      let best = -1;
      let bestDistance = Infinity;
      let flip = false;
      pending.forEach((candidate, index) => {
        const s = candidate.seg;
        const reach = jointReach(last.seg, s, width);
        const forward = Math.hypot(s.x1 - end.x, s.y1 - end.y);
        const backward = Math.hypot(s.x2 - end.x, s.y2 - end.y);
        if (forward <= reach && forward < bestDistance) { best = index; bestDistance = forward; flip = false; }
        if (backward <= reach && backward < bestDistance) { best = index; bestDistance = backward; flip = true; }
      });
      if (best < 0) break;
      const [taken] = pending.splice(best, 1);
      run.push({ seg: taken.seg, flipped: flip });
    }
    runs.push(run);
  }
  return runs;
}

/** One run of segments as its pieces: a node list, with a width and the handles the curves need. */
function bandsOfRun(run, lane, roadWidth) {
  const bands = [];
  let nodes = null;
  let previousEnd = null;
  let surface = DEFAULT_SURFACE;

  const flush = () => {
    if (!nodes || nodes.length < 2) { nodes = null; return; }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    let closed = false;
    if (nodes.length > 2 && Math.hypot(last.x - first.x, last.y - first.y) <= TOLERANCE + JOINT) {
      // A circuit: the closing edge is the last segment, so its arrival handle belongs to the first
      // node, and the repeated point goes away. Its width stays the first edge's, which is where the
      // lap is read from — the closing edge ramps into it.
      if (last.hIn) first.hIn = last.hIn;
      nodes.pop();
      closed = true;
    }
    bands.push({ lane, closed, width: roadWidth, nodes, ...surfaceField(surface) });
    nodes = null;
  };

  for (const { seg, flipped } of run) {
    const edge = flipped ? flipEdge(segEdge(seg)) : segEdge(seg);
    const joint = previousEnd
      ? Math.hypot(edge.a.x - previousEnd.x, edge.a.y - previousEnd.y) <= JOINT
      : false;
    // Two segments that don't share their endpoint are two roads (the export can leave a few units
    // between the pieces of a lane): they are never welded here, or the file would describe a road
    // that is not the one on the map. Only an exact joint becomes a single node. A change of surface
    // also breaks the band: the style is a property of the piece, and a piece is painted in one look.
    const ownSurface = surfaceOf(seg);
    if (!joint || (nodes && ownSurface !== surface)) {
      flush();
      surface = ownSurface;
      nodes = [{ x: edge.a.x, y: edge.a.y, w: seg.width ?? roadWidth }];
    }
    const from = nodes[nodes.length - 1];
    if (!edge.straight) from.hOut = { x: edge.c1.x, y: edge.c1.y };
    // The width of a node is the width of the edge ARRIVING at it: a segment is drawn as a band whose
    // width ramps from the node it starts at to the node it ends at, so a roomy straight into a
    // narrow chicane is one edge instead of a step at a joint nobody can see.
    nodes.push({ x: edge.b.x, y: edge.b.y, w: seg.width ?? roadWidth });
    if (!edge.straight) nodes[nodes.length - 1].hIn = { x: edge.c2.x, y: edge.c2.y };
    previousEnd = { x: edge.b.x, y: edge.b.y };
  }
  flush();
  return bands;
}

function flipEdge(edge) {
  return {
    a: edge.b,
    b: edge.a,
    c1: edge.c2,
    c2: edge.c1,
    straight: edge.straight,
  };
}

/** Every piece of the track: the bands (one per lane) and the areas, in file order. */
export function buildPieces({ segs = [], areas = [], roadWidth } = {}) {
  const pieces = [];
  const lanes = [];
  for (const seg of segs) {
    const lane = seg.lane ?? 'main';
    if (!lanes.includes(lane)) lanes.push(lane);
  }
  for (const lane of lanes) {
    const own = segs.filter((s) => (s.lane ?? 'main') === lane);
    const width = modalWidth(own) ?? roadWidth;
    let index = 0;
    for (const run of chainSegments(own, width)) {
      for (const band of bandsOfRun(run, lane, width)) {
        // The pit lane's line is yellow in game, and the lane's own id is what says so (that is the
        // editor's `pit` lane, next to `mainpit` for the lane the box itself belongs to).
        pieces.push({ id: index ? `${lane}_${index}` : lane, kind: 'band', ...band, ...(lane === 'pit' && { pit: true }) });
        index += 1;
      }
    }
  }
  for (const area of areas) {
    const nodes = (area.points ?? []).map((node) => ({
      x: node.x,
      y: node.y,
      ...(node.hIn && { hIn: { x: node.hIn.x, y: node.hIn.y } }),
      ...(node.hOut && { hOut: { x: node.hOut.x, y: node.hOut.y } }),
    }));
    if (nodes.length < 3) continue;
    pieces.push({
      id: area.id,
      kind: 'area',
      closed: true,
      ...(area.pit && { pit: true }),
      ...surfaceField(surfaceOf(area)),
      nodes,
    });
  }
  return pieces;
}

/** The width a lane is drawn at: the one most of its segments use. */
function modalWidth(segs) {
  const freq = new Map();
  for (const s of segs) if (s.width != null) freq.set(s.width, (freq.get(s.width) ?? 0) + 1);
  let best = null;
  let bestCount = 0;
  for (const [width, count] of freq) if (count > bestCount) { best = width; bestCount = count; }
  return best;
}

/** The whole file: the pieces plus everything that is not road. Coordinates are rounded to whole
 *  units here — the file is the map, and half a unit is nothing the editor can show or the game can
 *  use, while a whole number keeps it readable and diffable. */
export function toTrackFile(state) {
  const { pieces = [], width, height, grid, start, ...rest } = state;
  // The starting grid is ONE field (`grid`): the line plus `angle`, the way the cars face. A lone
  // `start` marker (a point and an angle, no line) folds its angle into the line.
  const gridOut = grid && { ...grid, ...(grid.angle == null && start?.angle != null && { angle: start.angle }) };
  return {
    version: FORMAT_VERSION,
    ...rest,
    ...(width != null && { width }),
    ...(height != null && { height }),
    ...(gridOut && { grid: gridOut }),
    pieces: pieces.map(roundPiece),
  };
}

function roundPiece(piece) {
  const nodes = (piece.nodes ?? []).map((node) => ({
    x: Math.round(node.x),
    y: Math.round(node.y),
    ...(node.w != null && { w: Math.round(node.w) }),
    ...(node.hIn && { hIn: { x: Math.round(node.hIn.x), y: Math.round(node.hIn.y) } }),
    ...(node.hOut && { hOut: { x: Math.round(node.hOut.x), y: Math.round(node.hOut.y) } }),
  }));
  return { ...piece, ...(piece.width != null && { width: Math.round(piece.width) }), nodes };
}

/** The reverse: a file back into the editor's state (segments, areas, and the rest untouched). */
export function fromTrackFile(file) {
  const segs = [];
  const areas = [];
  let nextId = 1;

  for (const piece of file.pieces ?? []) {
    const nodes = piece.nodes ?? [];
    if (piece.kind === 'area') {
      areas.push({
        id: piece.id ?? areas.length + 1,
        closed: true,
        ...(piece.pit && { pit: true }),
        ...(piece.surface && { surface: piece.surface }),
        points: nodes.map((node) => ({
          x: node.x,
          y: node.y,
          ...(node.hIn && { hIn: { x: node.hIn.x, y: node.hIn.y } }),
          ...(node.hOut && { hOut: { x: node.hOut.x, y: node.hOut.y } }),
        })),
      });
      continue;
    }
    const closed = piece.closed === true;
    const count = closed ? nodes.length : nodes.length - 1;
    for (let i = 0; i < count; i++) {
      const edge = edgeControls(nodes, i, closed);
      if (!edge) continue;
      const control = quadraticOf(edge);
      segs.push({
        id: nextId++,
        x1: edge.a.x, y1: edge.a.y,
        x2: edge.b.x, y2: edge.b.y,
        width: nodes[i].w ?? piece.width ?? file.roadWidth,
        lane: piece.lane ?? 'main',
        ...(piece.surface && { surface: piece.surface }),
        ...(control ? { cx: control.x, cy: control.y }
          : { hOut: { x: edge.c1.x, y: edge.c1.y }, hIn: { x: edge.c2.x, y: edge.c2.y } }),
      });
    }
  }
  return {
    id: file.id,
    name: file.name,
    width: file.width,
    height: file.height,
    roadWidth: file.roadWidth,
    // The start is `grid` now: its two ends, plus `angle` for the facing (the game reads it back the
    // same way). The pose itself is the line's midpoint, so it is not stored.
    grid: file.grid ?? file.startingGridLine ?? null,
    pitBox: file.pitBox,
    checkpoints: file.checkpoints ?? [],
    obstacles: file.obstacles ?? [],
    segs,
    areas,
  };
}
